// Geometry and colour of the RallyPD global mobile template — the chrome every
// dark page wears on a phone: the amber "Better on desktop" advisory pinned to
// the top, the back bar a sub page carries in place of the app topbar, and the
// tab that toggles Manage mode from the right edge.
//
// One module because the three pieces are dimensioned against each other: the
// banner's height is the offset the topbar and the scroll container are pushed
// down by, and the back bar sits directly beneath it.
import { DARK_TABLE_BORDER, DARK_TEXT_LIGHT, DARK_TEXT_MUTED } from './dark-tokens'

/**
 * The width below which the app is "a phone", for JavaScript that has to agree
 * with CSS about it.
 *
 * 767.98px, not 768px: Chakra's `md` starts AT 768, and Bootstrap's
 * `media-breakpoint-down(sm)` — which the layout SCSS uses — ends at 767.98. A
 * query written `(max-width: 768px)` is true on the one width where both of
 * those say desktop, and everything keyed off it disagreed with everything keyed
 * off them. On an iPad mini that straddle hid the sidebar while the page still
 * reserved its 220px column, and put a phone's back bar under the desktop topbar.
 */
export const MOBILE_MEDIA_QUERY = '(max-width: 767.98px)'
/** The same boundary for `window.innerWidth` checks. */
export const MOBILE_MAX_WIDTH = 767.98

/**
 * var(--Button-Gradient-1) — the darker navy of the two design gradients.
 *
 * Distinct from `AUTH_MOBILE_BUTTON_GRADIENT` (Button-Gradient-2), which is the
 * brighter primary call-to-action. This one reads as chrome rather than an
 * action, which is why the edge tab carries it.
 */
export const BUTTON_GRADIENT_1 = 'linear-gradient(180deg, #004988 0%, #00417B 37.92%, #003366 100%)'

/**
 * Height of the "Better on desktop" advisory, published as a CSS custom
 * property (`--mobile-banner-h`) while the bar is on screen so the fixed topbar
 * and the `.content-page` padding can both be offset by it.
 */
export const MOBILE_BANNER_HEIGHT = '36px'
/** The custom property the banner sets and the layout SCSS reads. */
export const MOBILE_BANNER_HEIGHT_VAR = '--mobile-banner-h'

/** Warning/500 — the amber the advisory and the Draft tag share. */
export const MOBILE_BANNER_BG = '#F2B134'
/** Dark brown, so the label and the monitor glyph read as one mark on the amber. */
export const MOBILE_BANNER_FG = '#3D2800'

/**
 * The back bar a sub page draws in place of the app topbar. 8px/16px with an
 * 8px gap between back arrow, title, status tag and the page's own actions.
 */
export const MOBILE_SUB_HEADER_BAR = {
  padding: '8px 16px',
  gap: '8px',
  alignItems: 'center',
  borderBottom: `1px solid ${DARK_TABLE_BORDER}`
} as const

/** 7/Medium — the design's type ramp for a sub page's name. Clamped to one line. */
export const MOBILE_SUB_HEADER_TITLE = {
  fontFamily: 'Poppins',
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '28px',
  color: DARK_TEXT_LIGHT
} as const

/** Shared shape of the status tag beside the title; only the tone differs. */
export const MOBILE_SUB_HEADER_TAG = {
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '10px',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase'
} as const

/**
 * Tone of the status tag. The status decides the colour: amber for work that is
 * not public yet, muted grey for work that has been put away, green for work
 * that is live.
 */
export type MobileSubHeaderTagTone = 'draft' | 'archived' | 'published' | 'neutral'

export const MOBILE_SUB_HEADER_TAG_TONES: Record<MobileSubHeaderTagTone, { background: string; color: string }> = {
  draft: { background: 'rgba(242, 177, 52, 0.13)', color: MOBILE_BANNER_BG },
  archived: { background: 'rgba(194, 199, 214, 0.12)', color: DARK_TEXT_MUTED },
  published: { background: 'rgba(73, 185, 128, 0.13)', color: '#49B980' },
  neutral: { background: 'rgba(194, 199, 214, 0.12)', color: DARK_TEXT_LIGHT }
}

/**
 * The tab that toggles Manage mode, pinned to the right edge of the viewport.
 *
 * A phone has no room for the desktop "Manage / Done" pill next to a breadcrumb,
 * so the control leaves the header and becomes a half-tab hanging off the edge:
 * square, rounded only on the side that faces the page.
 */
export const MOBILE_MANAGE_TAB = {
  position: 'fixed',
  right: 0,
  top: '240px',
  zIndex: 15,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  minWidth: '40px',
  height: '40px',
  padding: 0,
  border: 'none',
  borderRadius: '8px 0 0 8px',
  background: BUTTON_GRADIENT_1
} as const
